import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
} from 'react';
import { LogLevel, useSeqLog } from '@/hooks/telemetry/use-seq-log';
import {
  buildHistorySentinelState,
  getCurrentHistoryUrl,
  getHistorySentinelMetadata,
  stripHistorySentinelState,
} from './history-reconciler-state';
import { processPopstateEvent } from './history-reconciler-popstate';
import type {
  ClosePriorityLayers,
  HistoryReconcilerContextValue,
  HistoryReconcilerLogger,
  ModalStackHistoryAdapter,
  MutableValueRef,
} from './history-reconciler-types';
import { useModalStack } from './use-modal-stack';

const REF_CLICK_DEBUG_FLOW = 'ref-click-debug';
const HRP_LOG_COMPONENT_NAME = 'HistoryReconcilerProvider';

interface UseHistoryReconcilerValueInput {
  ownerKey: string;
}

interface ForeignSentinelCleanupInput {
  ownerIdRef: MutableValueRef<string>;
}

interface CloseModalStackPriorityLayersInput {
  count: number;
  modalStackRef: MutableValueRef<ModalStackHistoryAdapter>;
}

interface HistoryBackNavigationInput {
  count: number;
  closePriorityLayers: ClosePriorityLayers;
  pendingBrowserBackRef: MutableValueRef<number>;
}

interface BrowserHistoryDepthSyncInput {
  desiredDepth: number;
  ownerIdRef: MutableValueRef<string>;
  browserDepthRef: MutableValueRef<number>;
  pendingBrowserBackRef: MutableValueRef<number>;
  suppressCountRef: MutableValueRef<number>;
  logger: HistoryReconcilerLogger;
}

interface RegisterPopstateReconciliationInput {
  ownerIdRef: MutableValueRef<string>;
  modalStackRef: MutableValueRef<ModalStackHistoryAdapter>;
  browserDepthRef: MutableValueRef<number>;
  pendingBrowserBackRef: MutableValueRef<number>;
  suppressCountRef: MutableValueRef<number>;
  closePriorityLayers: ClosePriorityLayers;
  logger: HistoryReconcilerLogger;
}

interface PushBrowserHistorySentinelsInput {
  desiredDepth: number;
  ownerIdRef: MutableValueRef<string>;
  browserDepthRef: MutableValueRef<number>;
  logger: HistoryReconcilerLogger;
}

interface RollBackBrowserHistorySentinelsInput {
  desiredDepth: number;
  browserDepthRef: MutableValueRef<number>;
  pendingBrowserBackRef: MutableValueRef<number>;
  suppressCountRef: MutableValueRef<number>;
  logger: HistoryReconcilerLogger;
}

export function useHistoryReconcilerValue(
  input: UseHistoryReconcilerValueInput,
): HistoryReconcilerContextValue {
  const modalStack = useModalStack();
  const reactId = useId();
  const ownerIdRef = useRef(`session-history:${input.ownerKey}:${reactId}`);
  const modalStackRef = useRef<ModalStackHistoryAdapter>(modalStack);
  const browserDepthRef = useRef(0);
  const suppressCountRef = useRef(0);
  const pendingBrowserBackRef = useRef(0);
  const logger = useSeqLog(HRP_LOG_COMPONENT_NAME, { flow: REF_CLICK_DEBUG_FLOW });

  useEffect(() => {
    modalStackRef.current = modalStack;
  }, [modalStack]);

  useEffect(() => {
    cleanupForeignHistorySentinel({ ownerIdRef });
  }, []);

  const closePriorityLayers = useCallback((count: number): number => {
    return closeModalStackPriorityLayers({ count, modalStackRef });
  }, []);

  const goBack = useCallback((count = 1): void => {
    goBackThroughHistory({ count, closePriorityLayers, pendingBrowserBackRef });
  }, [closePriorityLayers]);

  const desiredDepth = modalStack.depth;

  useEffect(() => {
    syncBrowserHistoryDepth({
      desiredDepth,
      ownerIdRef,
      browserDepthRef,
      pendingBrowserBackRef,
      suppressCountRef,
      logger,
    });
  }, [desiredDepth, logger]);

  useEffect(() => {
    return registerPopstateReconciliation({
      ownerIdRef,
      modalStackRef,
      browserDepthRef,
      pendingBrowserBackRef,
      suppressCountRef,
      closePriorityLayers,
      logger,
    });
  }, [closePriorityLayers, logger]);

  return useMemo<HistoryReconcilerContextValue>(() => ({ goBack }), [goBack]);
}

function closeModalStackPriorityLayers(input: CloseModalStackPriorityLayersInput): number {
  if (input.count <= 0) {
    return 0;
  }

  let remainingCount = input.count;
  const closedModals = input.modalStackRef.current.closeN(remainingCount);
  remainingCount -= closedModals;

  return input.count - remainingCount;
}

function goBackThroughHistory(input: HistoryBackNavigationInput): void {
  const normalizedCount = Math.max(0, Math.floor(input.count));
  if (normalizedCount <= 0) {
    return;
  }

  const closedLayerCount = input.closePriorityLayers(normalizedCount);
  const remainingCount = normalizedCount - closedLayerCount;
  if (remainingCount > 0) {
    if (closedLayerCount > 0) {
      input.pendingBrowserBackRef.current += remainingCount;
      return;
    }

    window.history.go(-remainingCount);
  }
}

function syncBrowserHistoryDepth(input: BrowserHistoryDepthSyncInput): void {
  input.logger("HRP-1 depth-sync effect fired", {
    desiredDepth: input.desiredDepth,
    browserDepth: input.browserDepthRef.current,
    pendingBack: input.pendingBrowserBackRef.current,
    currentUrl: `${window.location.pathname}${window.location.search}`,
  }, LogLevel.Info);

  if (input.desiredDepth > input.browserDepthRef.current) {
    pushBrowserHistorySentinels({
      desiredDepth: input.desiredDepth,
      ownerIdRef: input.ownerIdRef,
      browserDepthRef: input.browserDepthRef,
      logger: input.logger,
    });
    return;
  }

  if (input.desiredDepth < input.browserDepthRef.current) {
    rollBackBrowserHistorySentinels({
      desiredDepth: input.desiredDepth,
      browserDepthRef: input.browserDepthRef,
      pendingBrowserBackRef: input.pendingBrowserBackRef,
      suppressCountRef: input.suppressCountRef,
      logger: input.logger,
    });
  }
}

function pushBrowserHistorySentinels(input: PushBrowserHistorySentinelsInput): void {
  const depthToAdd = input.desiredDepth - input.browserDepthRef.current;
  input.logger("HRP-2 depth-sync: PUSH sentinel(s)", {
    depthToAdd,
    urlBefore: `${window.location.pathname}${window.location.search}`,
  }, LogLevel.Warning);

  for (let index = 0; index < depthToAdd; index += 1) {
    const nextDepth = input.browserDepthRef.current + index + 1;
    window.history.pushState(
      buildHistorySentinelState(window.history.state, {
        depth: nextDepth,
        owner: input.ownerIdRef.current,
      }),
      '',
      getCurrentHistoryUrl(),
    );
  }

  input.browserDepthRef.current = input.desiredDepth;
}

function rollBackBrowserHistorySentinels(input: RollBackBrowserHistorySentinelsInput): void {
  const depthToRemove = input.browserDepthRef.current - input.desiredDepth;
  if (depthToRemove <= 0) {
    return;
  }

  const historyBackCount = depthToRemove + input.pendingBrowserBackRef.current;
  input.logger("HRP-3 depth-sync: ROLL BACK via history.go(-N)", {
    depthToRemove,
    pendingBack: input.pendingBrowserBackRef.current,
    totalHistoryBack: historyBackCount,
    urlBefore: `${window.location.pathname}${window.location.search}`,
  }, LogLevel.Error);
  input.pendingBrowserBackRef.current = 0;
  input.browserDepthRef.current = input.desiredDepth;
  input.suppressCountRef.current += 1;
  window.history.go(-historyBackCount);
  input.logger("HRP-3a depth-sync: history.go() called, suppressCount incremented", {
    suppressCount: input.suppressCountRef.current,
  }, LogLevel.Info);
}

function cleanupForeignHistorySentinel(input: ForeignSentinelCleanupInput): void {
  const currentSentinelMetadata = getHistorySentinelMetadata(window.history.state);
  if (!currentSentinelMetadata || currentSentinelMetadata.owner === input.ownerIdRef.current) {
    return;
  }

  window.history.replaceState(
    stripHistorySentinelState(window.history.state),
    '',
    getCurrentHistoryUrl(),
  );
}

function registerPopstateReconciliation(input: RegisterPopstateReconciliationInput): () => void {
  const handlePopstate = (event: PopStateEvent): void => {
    processPopstateEvent({
      event,
      ownerIdRef: input.ownerIdRef,
      modalStackRef: input.modalStackRef,
      browserDepthRef: input.browserDepthRef,
      pendingBrowserBackRef: input.pendingBrowserBackRef,
      suppressCountRef: input.suppressCountRef,
      closePriorityLayers: input.closePriorityLayers,
      logger: input.logger,
    });
  };

  window.addEventListener('popstate', handlePopstate);
  return () => {
    window.removeEventListener('popstate', handlePopstate);
  };
}
